'use client';

import React, { createContext, useContext, useState, useEffect } from 'react';
import { MAX_DISPLAYED_NOTIFICATION, LOCAL_STORAGE_KEY_NOTIFICATION } from '@/constants';
import { useGlobal } from '@/contexts/GlobalContext';
import { useModal } from '@/contexts/ModalContext';
import { getNotifications } from '@/repositories/notifications';
import { NotificationModel, NotificationWrapperModel } from '@/types/notifications';

// 通知に関するデータの型
interface NotificationContextData {
  myNotification: NotificationWrapperModel | undefined;
  allConfirmed: boolean;
  handleMessageConfirmed: (id: string) => void;
  handleAnnouncementConfirmed: (id: string) => void;
  openModal: (content: React.ReactNode) => void;
}

// NotificationContextの作成
const NotificationContext = createContext<NotificationContextData | undefined>(undefined);

// NotificationProviderの定義
export const NotificationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  /**
   * react contecxt
   */
  const context = useGlobal();
  const { openModal } = useModal();

  /**
   * useState
   */
  const [myNotification, setMyNotification] = useState<NotificationWrapperModel | undefined>(undefined);
  const [allConfirmed, setAllConfirmed] = useState<boolean>(true);

  const fromLocalStorage = (): NotificationWrapperModel[] => {
    const storageItems = localStorage.getItem(LOCAL_STORAGE_KEY_NOTIFICATION);
    return storageItems ? (JSON.parse(storageItems) as NotificationWrapperModel[]) : [];
  };

  const syncLocalStorage = (items: NotificationWrapperModel[]) => {
    localStorage.setItem(LOCAL_STORAGE_KEY_NOTIFICATION, JSON.stringify(items));
  };

  const myIndexOfStorageItems = (notification: NotificationWrapperModel[]) => {
    return notification.findIndex((s) => s.userId === context.user?.id);
  };

  const getMyStorageItems = (notification: NotificationWrapperModel[]): NotificationWrapperModel | undefined => {
    const index = myIndexOfStorageItems(notification);
    return index !== -1 ? notification[index] : undefined;
  };

  /**
   * ローカルストレージと新しく取得した通知をマージしてローカルストレージ保存用の通知を返却
   */
  const updateStorageItems = (
    oldStorageItems: NotificationWrapperModel[],
    newNotifications: NotificationWrapperModel
  ) => {
    const index = myIndexOfStorageItems(oldStorageItems);
    const updatedItems = [...oldStorageItems];

    // 自分の通知がローカルストレージに無い場合は取得した自分の通知を設定
    if (index === -1) {
      updatedItems.push(newNotifications);
      // 既存通知がある場合
    } else {
      // 新しい通知（通常のお知らせ）を先頭に設定
      updatedItems[index]!.messages = [...newNotifications.messages, ...updatedItems[index]!.messages];

      // 新しい通知（重要なお知らせ）は都度取得したものを設定する
      const oldAnnouncements: NotificationModel[] = updatedItems[index]!.announcements;
      updatedItems[index]!.announcements = [...newNotifications.announcements];

      // 重要な通知の確認済みフラグを設定（新しい情報で上書きされるので既存を参照し、再設定する）
      updatedItems[index]!.announcements.forEach((announcement: NotificationModel, i: number) => {
        const oldAnnouncement = oldAnnouncements.find((oldAnnouncement) => oldAnnouncement.id === announcement.id);
        if (oldAnnouncement && oldAnnouncement.confirmed) {
          updatedItems[index]!.announcements[i]!.confirmed = true;
        }
      });

      // 最終フェッチ時刻を更新
      updatedItems[index]!.fetched_at = newNotifications.fetched_at;

      // 最大20件の最新通知を見せるので古い通知は除外する。
      if (
        updatedItems[index]!.messages.length + updatedItems[index]!.announcements.length >
        MAX_DISPLAYED_NOTIFICATION
      ) {
        updatedItems[index]!.messages.splice(MAX_DISPLAYED_NOTIFICATION - updatedItems[index]!.announcements.length);
      }
    }

    return updatedItems;
  };

  /**
   * お知らせを確認済みにする
   */
  const handleMessageConfirmed = (id: string) => {
    const updatedMessages = myNotification?.messages.map((m) => (m.id === id ? { ...m, confirmed: true } : m));
    // ローカルストレージから取得済み通知を取得
    const storedItems = fromLocalStorage();
    const updatedItems = [...storedItems];
    if (updatedMessages) {
      updatedItems[myIndexOfStorageItems(updatedItems)]!.messages = updatedMessages;
      setMyNotification(getMyStorageItems(updatedItems));
      syncLocalStorage(updatedItems);
    }
  };

  /**
   * 重要なお知らせを確認済みにする
   */
  const handleAnnouncementConfirmed = (id: string) => {
    const updatedAnnouncements = myNotification?.announcements.map((a) =>
      a.id === id ? { ...a, confirmed: true } : a
    );
    // ローカルストレージから取得済み通知を取得
    const storedItems = fromLocalStorage();
    const updatedItems = [...storedItems];
    if (updatedAnnouncements) {
      updatedItems[myIndexOfStorageItems(updatedItems)]!.announcements = updatedAnnouncements;
      setMyNotification(getMyStorageItems(updatedItems));
      syncLocalStorage(updatedItems);
    }
  };

  /**
   * 全ての通知を確認済みかどうか
   */
  const isAllConfirmed = (notification: NotificationWrapperModel): boolean => {
    return (
      notification.messages.every((message) => message.confirmed) &&
      notification.announcements.every((announcement) => announcement.confirmed)
    );
  };

  // 通知をフェッチするかどうか
  // NOTE: 最終フェッチ時刻から10分経過していたら通知をフェッチする。
  const canFetch = (lastFetchedAt: Date) => {
    // NOTE:クライアントサイドのnew Dateはローカルマシンに依存する
    const localTime = new Date();
    const timeDifference = Math.abs(localTime.getTime() - lastFetchedAt.getTime());
    const minutesDifference = timeDifference / (1000 * 60);

    // console.log('localTime:')
    // console.log(localTime)
    // console.log('lastFetchedAt:')
    // console.log(lastFetchedAt)
    // console.log('differrence:')
    // console.log(minutesDifference)

    // 差分が10分以上であれはture
    return minutesDifference >= 10;
  };

  const setInitialMyNotifications = async () => {
    // ローカルストレージから取得済み通知を取得
    const storedItems = fromLocalStorage();
    // ローカルストレージの通知データから自分の通知のみ取得
    const myItems = getMyStorageItems(storedItems);

    // 自分の通知を状態に設定する(ローカールストレージの内容で一旦初期設定しておく)
    setMyNotification(myItems);
    // すべての通知が確認済みかどうかをチェック(ローカールストレージの内容で一旦初期設定しておく)
    setAllConfirmed(myItems ? isAllConfirmed(myItems) : true);

    // 通知の最終取得から1時間以内の場合はfetchしない。
    // NOTE: 環境差分に注意。vercelの本番環境はUTC、開発環境は日本時刻（日本時刻に９時間の考慮がされるのでズレる）。
    if (myItems && !canFetch(new Date(myItems.fetched_at))) return;

    try {
      // notificationsからidのみを抽出する
      const idLabels = myItems?.messages.map((m: NotificationModel) => m.id) || [];
      // POSTパラメータを設定
      const postCurrentIdLabels = { notification_idlabels: idLabels };
      // 通知をDBから取得
      const res = await getNotifications(postCurrentIdLabels, context.csrftoken || '');
      if (!res.ok) {
        throw new Error(res.detail);
      }

      if (res.data == null) return;

      // 既存通知と新しい通知をマージした保存用通知の取得
      const updatedItems = updateStorageItems(storedItems, res.data.notifications);

      // 自分宛の通知を設定
      // NOTE: 既存通知と新しい通知をマージした通知updatedItemsから設定する
      const myNewStrageItem = getMyStorageItems(updatedItems);

      // 自分の通知を状態に設定する(ローカルストレージ＆最新のフェッチデータ)
      setMyNotification(myNewStrageItem);

      // すべての通知が確認済みかどうかをチェック(ローカルストレージ＆最新のフェッチデータ)
      setAllConfirmed(myNewStrageItem ? isAllConfirmed(myNewStrageItem) : true);

      // 最新の通知状態をローカルストレージに保存(最新通知をフェッチしているので更新する)
      syncLocalStorage(updatedItems);
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    // 画面リロードした際、Context.userが存在せず、notificationのマージ処理で重複したUserIdのデータを作成してしまうバグがあった。
    // また3回のnotificationのPOST通信が走ってしまっていた。これはおそらくroot layoutのサイドメニュー、sdヘッダー、ルーティングのglobal contextの更新によって
    // 発生していたとおもわれる。
    // context.userが設定されている場合にfetchData()することで、通信が一回となり、UserId重複データの作成されることはなくなった。
    context.user != undefined && void setInitialMyNotifications();

    // NOTE: なぜcontextを指定しているか？
    // fetchData()処理内のmyIndexOfStorageItems()（ストレージから自分の通知のみを抽出する処理）がGlobalContextに依存している。
    // NotificationContextはヘッダー、サイドメニューからも呼ばれる。
    // 各IndexでGlobalContextを都度更新（ログインチェックのため）している。
    // contextを依存配列に追加することで各エンドポイントを叩いた際に、notificationAPIがコールされる
    // 現状Notificaitonの状態管理についてはreact contextで行なっているので上記のような挙動となってしまうが、他に良い管理方法があれば
    // リファクタしたい。
  }, [context]);

  useEffect(() => {
    const allConfirmed = myNotification ? isAllConfirmed(myNotification) : true;
    setAllConfirmed(allConfirmed);
  }, [myNotification]);

  // NotificationContext.Providerで提供する値
  const contextValue = {
    myNotification,
    allConfirmed,
    handleMessageConfirmed,
    handleAnnouncementConfirmed,
    openModal
  };

  return <NotificationContext.Provider value={contextValue}>{children}</NotificationContext.Provider>;
};

// useNotificationContextカスタムフック
export const useNotificationContext = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('NotificationContext must be used within a ModalProvider');
  }
  return context;
};
